import React, { useState, useEffect } from 'react';
import { Star, MessageSquare, User, Calendar, ChevronDown, ChevronUp, Search, Reply } from 'lucide-react';
import './TalentReviews.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface Review {
  id: number;
  reviewer_id: number;
  reviewer_name: string;
  reviewer_type: 'client' | 'customer';
  reviewer_profile_picture?: string;
  rating: number;
  review_text: string;
  response_text?: string;
  created_at: string;
  updated_at: string;
  status: 'active' | 'hidden' | 'reported';
  is_verified: boolean;
  helpful_count: number;
}

interface ReviewStats {
  totalReviews: number;
  averageRating: number;
  ratingDistribution: {
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
  };
  responseRate: number;
}

const TalentReviews: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [expandedReviews, setExpandedReviews] = useState<Set<number>>(new Set());
  const [respondingTo, setRespondingTo] = useState<number | null>(null);
  const [responseText, setResponseText] = useState('');
  const [submittingResponse, setSubmittingResponse] = useState(false);

  const itemsPerPage = 10;

  // Fetch reviews
  const fetchReviews = React.useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('skillseek_talent_token');
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        search: searchTerm,
        status: statusFilter,
        rating: ratingFilter
      });

      const response = await fetch(`${API_BASE_URL}/api/talent/auth/reviews?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch reviews: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setReviews(data.reviews || []);
        setStats(data.stats || null);
        setTotalPages(data.pagination?.totalPages || 1);
      } else {
        throw new Error(data.message || 'Failed to fetch reviews');
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter, ratingFilter]);

  // Submit response to review
  const submitResponse = async (reviewId: number) => {
    try {
      setSubmittingResponse(true);
      const token = localStorage.getItem('skillseek_talent_token');

      const response = await fetch(`${API_BASE_URL}/api/talent/auth/reviews/${reviewId}/respond`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ response_text: responseText.trim() })
      });

      if (!response.ok) {
        throw new Error('Failed to submit response');
      }

      const data = await response.json();
      
      if (data.success) {
        // Update the review in the local state
        setReviews(prevReviews => 
          prevReviews.map(review => 
            review.id === reviewId 
              ? { ...review, response_text: responseText.trim() }
              : review
          )
        );
        
        setRespondingTo(null);
        setResponseText('');
      } else {
        throw new Error(data.message || 'Failed to submit response');
      }
    } catch (error) {
      console.error('Error submitting response:', error);
      alert('Failed to submit response. Please try again.');
    } finally {
      setSubmittingResponse(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Render star rating
  const renderStars = (rating: number, size: number = 16) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        size={size}
        className={index < rating ? 'star-filled' : 'star-empty'}
      />
    ));
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getReviewerAvatar = (path: string | undefined | null): string => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const base = API_BASE_URL.replace(/\/api\/?$/, '');
    if (path.startsWith('/uploads/') || path.startsWith('uploads/')) {
      const cleanPath = path.startsWith('/') ? path.substring(1) : path;
      return `${base}/${cleanPath}`;
    }
    return `${base}/uploads/${path}`;
  };

  // Toggle expanded view
  const toggleExpanded = (reviewId: number) => {
    setExpandedReviews(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reviewId)) {
        newSet.delete(reviewId);
      } else {
        newSet.add(reviewId);
      }
      return newSet;
    });
  };

  // Get rating text
  const getRatingText = (rating: number) => {
    const texts = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];
    return texts[rating] || '';
  };

  if (loading && reviews.length === 0) {
    return (
      <div className="talent-reviews-container">
        <div className="reviews-header">
          <div className="reviews-header-content">
            <h2>Reviews & Ratings</h2>
            <p>Loading your reviews...</p>
          </div>
        </div>
        <div className="loading-spinner" />
      </div>
    );
  }

  if (error && reviews.length === 0) {
    return (
      <div className="talent-reviews-container">
        <div className="reviews-header">
          <div className="reviews-header-content">
            <h2>Reviews & Ratings</h2>
            <p>Manage and respond to client reviews</p>
          </div>
        </div>
        <div className="error-message">
          <p>{error}</p>
          <button onClick={fetchReviews} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="talent-reviews-container">
      {/* Header with Stats */}
      <div className="reviews-header">
        <div className="reviews-header-content">
          <h2>Reviews & Ratings</h2>
          <p>Manage and respond to client reviews</p>
        </div>
        
        {stats && (
          <div className="reviews-stats">
            <div className="stat-card">
              <div className="stat-icon">
                <Star size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.averageRating.toFixed(1)}</div>
                <div className="stat-label">Average Rating</div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon">
                <MessageSquare size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.totalReviews}</div>
                <div className="stat-label">Total Reviews</div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon">
                <Reply size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.responseRate}%</div>
                <div className="stat-label">Response Rate</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Rating Distribution */}
      {stats && (
        <div className="reviews-rating-distribution">
          <h3>Rating Distribution</h3>
          <div className="reviews-distribution-bars">
            {[5, 4, 3, 2, 1].map(rating => {
              const count = stats.ratingDistribution[rating as keyof typeof stats.ratingDistribution] || 0;
              const percentage = stats.totalReviews > 0 ? (count / stats.totalReviews) * 100 : 0;
              
              return (
                <div key={rating} className="reviews-distribution-row">
                  <div className="reviews-rating-label">
                    <div className="reviews-stars-wrapper">
                      {renderStars(rating, 12)}
                    </div>
                    <span className="reviews-rating-number">{rating}</span>
                  </div>
                  <div className="reviews-bar-container">
                    <div className="reviews-bar-background">
                      <div 
                        className="reviews-bar-fill"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                  <div className="reviews-count-label">{count}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="reviews-filters">
        <div className="filter-row">
          <div className="search-container">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search reviews..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-selects">
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="hidden">Hidden</option>
              <option value="reported">Reported</option>
            </select>
            
            <select 
              value={ratingFilter}
              onChange={(e) => setRatingFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Ratings</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>
          </div>
        </div>
      </div>

      {/* Reviews List */}
      <div className="reviews-list">
        {reviews.length === 0 ? (
          <div className="no-reviews">
            <MessageSquare size={48} />
            <h3>No Reviews Yet</h3>
            <p>When clients leave reviews for your services, they'll appear here.</p>
          </div>
        ) : (
          reviews.map(review => (
            <div key={review.id} className="review-card">
              <div className="review-header">
                <div className="reviewer-info">
                  <div className="reviewer-avatar">
                    {review.reviewer_profile_picture ? (
                      <img 
                        src={getReviewerAvatar(review.reviewer_profile_picture)}
                        alt={review.reviewer_name}
                      />
                    ) : (
                      <User size={20} />
                    )}
                  </div>
                  <div className="reviewer-details">
                    <div className="reviewer-name">{review.reviewer_name}</div>
                    <div className="review-meta">
                      <span className="reviewer-type">{review.reviewer_type}</span>
                      {review.is_verified && (
                        <span className="verified-badge">Verified</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="review-rating">
                  <div className="stars">
                    {renderStars(review.rating)}
                  </div>
                  <div className="rating-text">
                    {review.rating}/5 • {getRatingText(review.rating)}
                  </div>
                </div>
                
                <div className="review-date">
                  <Calendar size={14} />
                  {formatDate(review.created_at)}
                </div>
              </div>



              <div className="review-content">
                <div className={`review-text ${expandedReviews.has(review.id) ? 'expanded' : ''}`}>
                  {review.review_text}
                </div>
                
                {review.review_text.length > 200 && (
                  <button 
                    className="expand-button"
                    onClick={() => toggleExpanded(review.id)}
                  >
                    {expandedReviews.has(review.id) ? (
                      <>Show Less <ChevronUp size={16} /></>
                    ) : (
                      <>Show More <ChevronDown size={16} /></>
                    )}
                  </button>
                )}
              </div>

              {/* Response Section */}
              <div className="response-section">
                {review.response_text ? (
                  <div className="existing-response">
                    <div className="response-header">
                      <Reply size={16} />
                      <span>Your Response</span>
                    </div>
                    <div className="response-text">{review.response_text}</div>
                  </div>
                ) : (
                  <div className="response-actions">
                    {respondingTo === review.id ? (
                      <div className="response-form">
                        <textarea style={{width:"100%"}}
                          value={responseText}
                          onChange={(e) => setResponseText(e.target.value)}
                          placeholder="Write your response to this review..."
                          className="response-textarea"
                          rows={3}
                        />
                        <div className="response-buttons">
                          <button 
                            onClick={() => submitResponse(review.id)}
                            disabled={!responseText.trim() || submittingResponse}
                            className="submit-response-btn"
                          >
                            {submittingResponse ? 'Submitting...' : 'Submit Response'}
                          </button>
                          <button 
                            onClick={() => {
                              setRespondingTo(null);
                              setResponseText('');
                            }}
                            className="cancel-response-btn"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button 
                        onClick={() => setRespondingTo(review.id)}
                        className="respond-button"
                      >
                        <Reply size={16} />
                        Respond
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Review Status */}
              <div className="review-footer">
                <div className="review-status">
                  <span className={`status-badge status-${review.status}`}>
                    {review.status}
                  </span>
                  {review.helpful_count > 0 && (
                    <span className="helpful-count">
                      {review.helpful_count} people found this helpful
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          
          <div className="page-info">
            Page {currentPage} of {totalPages}
          </div>
          
          <button 
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default TalentReviews;
